/**
 * YUMIX — Knowledge Tracker (Encyclopédie YUMIX).
 *
 * Tracks which "Le saviez-vous?" nutrition facts the user has seen long
 * enough to actually read (we count a fact as "read" after it has been on
 * screen for 3+ seconds). Awards milestone badges at 10, 25, 50 and 71
 * facts read.
 *
 * Storage : single localStorage key `yumix_knowledge_v1` containing
 *   {
 *     read: [number, ...],            // indices of facts read
 *     unlocks: [10, 25, 50, 71]       // milestones already celebrated
 *   }
 *
 * Why client-only? Zero backend pressure, instant feedback, perfect for
 * an "engagement nudge" feature. Client power-users naturally accumulate
 * knowledge over weeks of scanning.
 */
import { NUTRITION_FACTS } from '../constants/nutritionFacts';

const STORAGE_KEY = 'yumix_knowledge_v1';
const READ_THRESHOLD_MS = 3000; // a fact counts as read after 3s on screen

const MILESTONES = [
  { count: 10, fr: '🌱 Apprenti Nutrition · 10 faits lus', en: '🌱 Nutrition Apprentice · 10 facts read', rarity: 'common' },
  { count: 25, fr: '📖 Étudiant Yumix · 25 faits lus', en: '📖 Yumix Student · 25 facts read', rarity: 'rare' },
  { count: 50, fr: '🎓 Encyclopédie Yumix · 50 faits lus', en: '🎓 Yumix Encyclopedia · 50 facts read', rarity: 'epic' },
  { count: 71, fr: '🏆 Maître Nutrition Yumix · Tous les faits lus', en: '🏆 Yumix Nutrition Master · All facts read', rarity: 'legendary' },
];

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { read: [], unlocks: [] };
    const parsed = JSON.parse(raw);
    return {
      read: Array.isArray(parsed.read) ? parsed.read : [],
      unlocks: Array.isArray(parsed.unlocks) ? parsed.unlocks : [],
    };
  } catch {
    return { read: [], unlocks: [] };
  }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota — silent */ }
}

/**
 * Mark a fact (by its index in the original NUTRITION_FACTS array) as read.
 * Returns the milestone object that was JUST unlocked, or null if no
 * milestone was crossed on this call.
 *
 * Accepts the original NUTRITION_FACTS index OR the fact object itself
 * (we'll resolve the index by reference equality on .src + .fr).
 */
export function markFactRead(factOrIndex) {
  let idx;
  if (typeof factOrIndex === 'number') {
    idx = factOrIndex;
  } else if (factOrIndex && typeof factOrIndex === 'object') {
    idx = NUTRITION_FACTS.findIndex((f) => f.fr === factOrIndex.fr && f.src === factOrIndex.src);
  } else {
    return null;
  }
  if (idx < 0 || idx >= NUTRITION_FACTS.length) return null;

  const state = loadState();
  if (state.read.includes(idx)) return null; // already counted, no milestone re-fire

  state.read.push(idx);
  let newlyUnlocked = null;
  for (const m of MILESTONES) {
    if (state.read.length >= m.count && !state.unlocks.includes(m.count)) {
      state.unlocks.push(m.count);
      newlyUnlocked = m;
      break; // only celebrate one per scan
    }
  }
  saveState(state);
  return newlyUnlocked;
}

/** Return the current count and progress towards next milestone. */
export function getKnowledgeProgress() {
  const state = loadState();
  const next = MILESTONES.find((m) => !state.unlocks.includes(m.count));
  return {
    read_count: state.read.length,
    total: NUTRITION_FACTS.length,
    next_milestone: next || null,
    unlocked: state.unlocks.slice().sort((a, b) => a - b),
  };
}

/** Reset (for QA / "reset progress" admin button). Never used in normal UX. */
export function resetKnowledge() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

export const KNOWLEDGE_MILESTONES = MILESTONES;
export const KNOWLEDGE_READ_THRESHOLD_MS = READ_THRESHOLD_MS;
